// Stimulus controller for the AI Pronunciations admin tab.
// Handles the per-type feedback popup: fetches the _feedback_records partial via AJAX,
// injects it into the overlay, and coordinates row highlighting with the shared
// ai-latin-value-reports controller (mounted on the same element).
import ApplicationController from "./application_controller"

export default class extends ApplicationController {
  static targets = [
    "row",                // All pronunciation table rows (for clearing highlights)
    "feedbackOverlay",    // Full-screen overlay for feedback popup
    "feedbackTitle",      // <h2> element for feedback type label (Correct/Incorrect/Neutral)
    "feedbackContext",    // Context bar showing Ottoman value, AI latin, dictionary, page
    "feedbackTableBody",  // <tbody> where feedback records are injected
    "searchForm",         // Search form for auto-submit on clear
  ];

  static values = {
    feedbackRecordsUrl: String,
    loadError: String,
  };

  connect() {
    this.refereshIcons();                // Initialize feather icons (inherited)
    this.highlightedRowId = null;        // Track which pronunciation row is highlighted

    // Get reference to shared ai-latin-value-reports controller for message popup coordination
    this.reportsController = this.application.getControllerForElementAndIdentifier(
      this.element, "ai-latin-value-reports"
    );
  }

  disconnect() {
    this.clearMainRowHighlight();
  }

  // Auto-submits search form when input is cleared via typing or browser X button
  handleSearchClear(event) {
    if (event.target.value.trim() !== "") return;
    if (!this.hasSearchFormTarget) return;
    if (!new URL(window.location.href).searchParams.get("search_query")?.trim()) return;

    this.searchFormTarget.submit();
  }

  // Prevents click events from propagating to overlay background
  stopPropagation(event) {
    event.stopPropagation();
  }

  // Opens the feedback popup by fetching records for the clicked count button.
  // Highlights the source pronunciation row and renders the popup content.
  openFeedbackPopup(event) {
    if (!this.hasFeedbackOverlayTarget) return;

    const button = event.currentTarget;
    this.setMainRowHighlight(button.dataset.rowId);

    const url = this.buildFeedbackRecordsUrl(button);
    this.fetchHtml(url)
      .then((html) => this.renderFeedbackPopup(html))
      .catch((error) => this.addFlashMessage(error.message || this.loadErrorValue));
  }

  // Parses the _feedback_records partial and injects title, context, and table rows
  // into the feedback popup. Also initializes tooltips for email copy icons.
  renderFeedbackPopup(html) {
    this.reportsController?.closeMessage();  // Close any open message popup first

    const wrapper  = document.createElement("div");
    wrapper.innerHTML = html;
    const titleEl    = wrapper.querySelector("[data-feedback-title]");
    const contextEl  = wrapper.querySelector("[data-feedback-context]");
    const rowsEl     = wrapper.querySelector("[data-feedback-rows] tbody");

    this.feedbackTitleTarget.textContent = titleEl?.dataset.feedbackTitle || "Feedback";
    this.feedbackContextTarget.replaceChildren(...Array.from(contextEl?.childNodes ?? []));
    this.feedbackTableBodyTarget.replaceChildren(...Array.from(rowsEl?.childNodes ?? []));
    this.feedbackOverlayTarget.classList.remove("d-none");
    this.refereshIcons();                    // Initialize feather icons in new content
    this.reportsController?.bindTooltips();  // Initialize Bootstrap tooltips
  }

  // Closes the feedback popup and clears all highlight states
  closeFeedbackPopup() {
    if (!this.hasFeedbackOverlayTarget) return;

    this.reportsController?.closeMessage();  // Close message popup if open
    this.feedbackOverlayTarget.classList.add("d-none");
    this.feedbackTableBodyTarget.replaceChildren();
    this.clearMainRowHighlight();
    this.reportsController?.clearRowHighlight();  // Clear feedback table row highlights
  }

  // Closes feedback popup when user clicks directly on the overlay background
  closeFeedbackOnOverlay(event) {
    if (event.target === this.feedbackOverlayTarget) this.closeFeedbackPopup();
  }

  // Highlights the pronunciation row that triggered the feedback popup
  setMainRowHighlight(rowId) {
    this.clearMainRowHighlight();
    const row = document.getElementById(rowId);
    if (!row) return;

    row.classList.add("ai-pronunciations-row-highlighted");
    this.highlightedRowId = rowId;
  }

  // Removes highlight from the previously highlighted pronunciation row
  clearMainRowHighlight() {
    if (!this.highlightedRowId) return;

    document.getElementById(this.highlightedRowId)?.classList.remove("ai-pronunciations-row-highlighted");
    this.highlightedRowId = null;
  }

  // Constructs the URL for fetching feedback records with query params
  buildFeedbackRecordsUrl(button) {
    const url = new URL(this.feedbackRecordsUrlValue, window.location.origin);
    url.searchParams.set("ottoman_spelling_id", button.dataset.ottomanSpellingId);
    url.searchParams.set("ai_latin_value",      button.dataset.aiLatinValue);
    url.searchParams.set("feedback_type",       button.dataset.feedbackType);
    return url;
  }

  // Fetches HTML partial from the server with error handling
  fetchHtml(url) {
    return fetch(url, {
      headers: {
        Accept:          "text/html",
        "X-CSRF-Token":  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
      }
    }).then(async (response) => {
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || this.loadErrorValue);
      }
      return response.text();
    });
  }
}
